import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React, { memo, useContext } from 'react';
import clsx from 'clsx';
import { AnimatedButton, Button, Link, DisclaimerText } from 'reaxl';
import { VisitorsAgreement } from 'reaxl-molecules';
import { Input, Checkbox } from 'reaxl-forms';
import { useFeatures } from '@atc/features';
import { sendClick } from 'reaxl-analytics';
import { smsOptClick } from 'reaxl-analytics-handlers';
import TCPADisclaimer from '../TCPADisclaimer';
import EmailStaticContent from '../EmailStaticContent';
import EmailContext from '../EmailContext';
function FinanceDisclaimer(_ref) {
  let {
    header,
    isSingleParagraph,
    subHeader
  } = _ref;
  const componentClass = isSingleParagraph ? 'span' : 'div';
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx(DisclaimerText, {
    componentClass: componentClass,
    weight: "bold"
  }, void 0, header, isSingleParagraph && ' '), /*#__PURE__*/_jsx(DisclaimerText, {
    componentClass: componentClass
  }, void 0, subHeader));
}
const customDisclaimerText = /*#__PURE__*/_jsx(DisclaimerText, {
  componentClass: "div",
  className: "margin-bottom-4"
}, void 0, "By clicking the button, you agree to the Autotrader", ' ', /*#__PURE__*/_jsx(Link, {
  href: "https://autotrader.com/legal/visitor-agreement",
  target: "_blank",
  rel: "noopener noreferrer"
}, void 0, "Visitor Agreement"), ' ', "and", ' ', /*#__PURE__*/_jsx(Link, {
  href: "https://autotrader.com/legal/privacy-statement",
  target: "_blank",
  rel: "noopener noreferrer"
}, void 0, "Privacy Statement"), ' ', "and that your contact and/or My Wallet information will be shared with the dealer and/or Car and Driver, including for their own advertising purposes. Each party's use of your information is subject to their privacy policy.");

/**
 *  EmailFormFooter component has the submit button, checkboxes and visitor agreement
 *
 */
const EmailFormFooter = /*#__PURE__*/memo(_ref2 => {
  let {
    animatedButton = false,
    buttonStyle = 'primary',
    ctaContext = '',
    inquiryProps = {},
    isButtonDisabled = false,
    isEditEmailModal = false,
    isIncentivesSheet,
    isOneClickEdit = false,
    walletOptIn = false,
    showFinanceDisclaimer = false,
    submitState = '',
    setMessageOptIn
  } = _ref2;
  const {
    uiContext,
    owner: {
      hasTCPA = false,
      tcpaDisclaimer = null,
      tcpaAuditVendor = null
    } = {}
  } = useContext(EmailContext);
  const {
    inquiry: inquiryConfig,
    validationMessages,
    financeDisclaimer
  } = EmailStaticContent;
  const {
    brand: [, {
      visitor_agreement_link: visitorAgreementLink
    }],
    email: [, {
      disable_submit_button: optimizelyDisableSubmit,
      show_custom_disclaimer_text: showCustomDisclaimer
    }],
    email_opt_in: [isOptInEnabled, {
      label: optInLabel
    }],
    sms_opt_in: [, {
      label: smsOptInLabel
    }],
    scripts: [enableScripts, {
      cadNitrous: enableCadNitrous
    }]
  } = useFeatures(['brand', 'email', 'email_opt_in', 'sms_opt_in', 'scripts']);
  const {
    inquirySelection
  } = inquiryProps;
  const isVHRLeadGate = uiContext === 'vehicleHistoryReport';
  const isTestDrive = uiContext === 'testDrive';
  let ctaText = 'Send Email';
  if (!['calculator', 'calculatorOfferSummary', 'calculatorDealSubmitted'].includes(ctaContext)) {
    ctaText = EmailStaticContent.cta.requestInfo;
  }
  if (inquiryConfig[inquirySelection]?.schedule || isTestDrive) {
    ctaText = 'Request Appointment';
  }
  if (ctaContext?.includes('calculator') && uiContext !== 'multi') {
    ctaText = EmailStaticContent.cta[ctaContext];
  }
  if (isEditEmailModal && uiContext !== 'multi') {
    ctaText = EmailStaticContent.cta.editOnly;
  }
  if (isVHRLeadGate) {
    ctaText = EmailStaticContent.cta[ctaContext];
  }
  const handleShowMessageOptIn = event => {
    const checked = event?.target?.checked;
    setMessageOptIn(checked);
    sendClick(smsOptClick, event, {
      checked
    });
  };
  let nitrousElementName;
  // only add data-nitrous-element-name for cad nitrous
  if (enableScripts && enableCadNitrous) {
    if (ctaText === EmailStaticContent.cta.checkAvailability || ctaText === 'Send Email') {
      nitrousElementName = 'Request Info (Submit Form)';
    } else if (ctaContext === 'calculatorOfferSummary') {
      nitrousElementName = 'Send Offer (Submit Form)';
    }
  }
  const renderSubmitCta = animatedButton ? /*#__PURE__*/_jsx(AnimatedButton, {
    block: true,
    bsStyle: buttonStyle,
    buttonState: submitState,
    buttonText: ctaText,
    className: clsx({
      'margin-bottom-3': !isVHRLeadGate,
      'margin-bottom-4': isVHRLeadGate
    }),
    disabled: isButtonDisabled || optimizelyDisableSubmit,
    formNoValidate: true,
    type: "submit",
    "data-nitrous-element-name": nitrousElementName
  }, "animated-submit-button") : /*#__PURE__*/_jsx(Button, {
    block: true,
    className: "margin-bottom-3",
    bsStyle: buttonStyle,
    disabled: isButtonDisabled || optimizelyDisableSubmit,
    formNoValidate: true,
    type: "submit",
    "data-nitrous-element-name": nitrousElementName
  }, void 0, ctaText);
  return /*#__PURE__*/React.createElement(React.Fragment, null, showFinanceDisclaimer && /*#__PURE__*/React.createElement(FinanceDisclaimer, Object.assign({}, financeDisclaimer, {
    isSingleParagraph: uiContext === 'calculatorOfferSummary'
  })), /*#__PURE__*/React.createElement(Input, Object.assign({
    type: "hidden",
    name: "textPot",
    id: "textPot",
    validations: validationMessages.hidden
  }, isVHRLeadGate && {
    formClassName: 'margin-bottom-1'
  })), showCustomDisclaimer && customDisclaimerText, !showCustomDisclaimer && /*#__PURE__*/_jsx(DisclaimerText, {
    withContrast: true,
    className: "display-inline-block margin-bottom-4"
  }, void 0, /*#__PURE__*/_jsx(VisitorsAgreement, {
    url: visitorAgreementLink || '',
    text: `By clicking "${ctaText}", you accept the terms of our `,
    className: "display-inline"
  }), walletOptIn && ' and agree that your My Wallet information will be shared with this dealer and used in accordance with its privacy policy.'), renderSubmitCta, !!setMessageOptIn && uiContext === 'default' && !hasTCPA && !isIncentivesSheet && /*#__PURE__*/_jsx(Checkbox, {
    name: "optIn",
    label: smsOptInLabel,
    rowClassName: "text-size-200",
    onChange: handleShowMessageOptIn,
    value: true
  }), isOptInEnabled && uiContext !== 'quote' && uiContext !== 'basic' && (uiContext !== 'edit' || !isOneClickEdit) && /*#__PURE__*/_jsx(Checkbox, {
    name: "optIn",
    label: optInLabel,
    labelClassName: "margin-bottom-0",
    value: true,
    rowClassName: "text-size-200"
  }), hasTCPA && /*#__PURE__*/_jsx(TCPADisclaimer, {
    disclaimer: tcpaDisclaimer,
    auditVendor: tcpaAuditVendor,
    className: "text-size-200"
  }));
});
export default EmailFormFooter;